import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from '../components/Toast';
import { jsonPost } from '../api/jsonRequest';
import { queryKeys } from '../query/queryKeys';
import { buildPollUrl } from '../query/queryParams';
import { usePollQuery } from '../query/usePollQuery';

export function useCompanyUsers({
    companyId,
    menuType,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl('/api/Company/GetAllUsersForCompany', companyId);

    return usePollQuery({
        queryKey: queryKeys.companyUsers.list(companyId, menuType),
        url,
        enabled,
        pauseRefetch,
    });
}

export function useSelectedCompanyUsers({
    companyId,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl('/api/Company/GetAllUsersForCompany', companyId);

    return usePollQuery({
        queryKey: queryKeys.companyUsers.selected(companyId),
        url,
        enabled,
        pauseRefetch,
    });
}

export function useAddUserToCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPost('/api/Account/AddUserToGroup', payload);
            return res;
        },
        onSuccess: (res) => {
            if (res.status === 202) {
                Toast.fire({ title: 'User Added', icon: 'success' });
            } else {
                Toast.fire({ title: res.data, icon: 'error' });
            }
            void queryClient.invalidateQueries({ queryKey: queryKeys.companyUsers.all() });
        },
    });
}

export function useNewUserToCompany() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ companyId, usr, role }) => {
            const newUser = await jsonPost(
                `/api/Account/CreateUser?companyId=${companyId}`,
                usr,
            );
            if (newUser.status !== 200) {
                throw new Error('Failed creating user');
            }
            const newRole = await jsonPost('/api/Account/AddUserToGroup', role);
            return newRole;
        },
        onSuccess: (newRole) => {
            if (newRole.status === 200) {
                Toast.fire({ title: 'User added', icon: 'success' });
            }
            void queryClient.invalidateQueries({ queryKey: queryKeys.companyUsers.all() });
        },
        onError: () => {
            Toast.fire({
                title: 'Failed creating user, email or username may be already in use',
                icon: 'error',
            });
        },
    });
}

export function useUpdateUserRole() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPost('/api/Account/UpdateUserRole', payload);
            if (res.status !== 200) {
                throw new Error('User Role not updated');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({
                icon: 'success',
                title: 'Role Updated.',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.companyUsers.all() });
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: 'User Role not updated check your entry and try again',
            });
        },
    });
}

export function useDeactivateEmployee() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const res = await jsonPost('/api/Account/DeactivateEmployee', payload);
            if (res.status !== 200) {
                throw new Error('Employee not updated');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({
                icon: 'success',
                title: 'Employee Updated.',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.companyUsers.all() });
        },
        onError: () => {
            Toast.fire({
                icon: 'error',
                title: 'Employee not updated try again.',
            });
        },
    });
}
